import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, Lock, Check, ArrowRight } from "lucide-react";

/**
 * UpgradeModal — shown when a user hits a plan quota.
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   resource: "reports" | "enhancements" | "downloads" | "crm_access"
 *   plan: string                — current plan key
 *   used / limit: numbers       — passed through from the backend 402 payload
 *   recommendedPlan?: string    — defaults based on resource
 */
const RESOURCE_TITLES = {
  reports: "You've used all your reports this period",
  enhancements: "Enhancement quota reached",
  downloads: "Download quota reached",
  crm_access: "CRM Pipeline is a CRM Elite feature",
};

const RESOURCE_HEROES = {
  reports: "Unlock more deep account intelligence — bigger pipeline, faster.",
  enhancements: "Stop limiting your insights. Re-enhance reports with new primary data anytime.",
  downloads: "Send polished, executive-grade PDFs without limits.",
  crm_access: "Track every meeting, proposal and win in one place — with team analytics.",
};

const PLAN_CARDS = [
  {
    key: "pro_monthly",
    name: "Pro Monthly",
    price: "₹250",
    period: "per month",
    perks: ["5 reports / month", "5 PDF downloads / month", "Unlimited enhancements"],
  },
  {
    key: "pro_yearly",
    name: "Pro Yearly",
    price: "₹2,750",
    period: "per year",
    perks: ["50 reports / year", "50 PDF downloads / year", "Unlimited enhancements", "Save ₹250"],
    popular: true,
  },
  {
    key: "crm_elite",
    name: "CRM Elite",
    price: "₹50,000",
    period: "per year",
    perks: ["100 reports / year", "100 PDF downloads / year", "Full CRM pipeline tracking", "Team analytics"],
    crm: true,
  },
];

export default function UpgradeModal({ open, onClose, resource = "reports", plan, used, limit }) {
  const title = RESOURCE_TITLES[resource] || "Upgrade required";
  const hero = RESOURCE_HEROES[resource] || "Unlock more of RADAR.";

  const recommendedKey = useMemo(() => {
    if (resource === "crm_access") return "crm_elite";
    if (resource === "downloads" || resource === "enhancements") return "pro_yearly";
    return "pro_yearly";
  }, [resource]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="bg-[#141414] border-[#262626] text-white sm:max-w-2xl"
        data-testid="upgrade-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl tracking-tight">
            <Lock className="w-5 h-5 text-[#FF9F0A]"/>
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <p className="text-sm text-[#A3A3A3] leading-relaxed">{hero}</p>

          {(typeof used === "number" && typeof limit === "number") && (
            <div className="bg-[#0A0A0A] border border-[#262626] rounded-sm p-3 flex items-center justify-between">
              <div className="kpi-label">Current plan · {plan?.replace("_", " ").toUpperCase() || "FREE"}</div>
              <div className="font-mono-data text-sm text-white">
                {used} / {limit} used
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {PLAN_CARDS.map((p) => (
              <div
                key={p.key}
                className={`border rounded-sm p-4 flex flex-col gap-3 transition-colors ${
                  p.key === recommendedKey
                    ? "border-[#0A84FF] bg-[#0A84FF]/5"
                    : "border-[#262626] bg-[#0A0A0A]"
                }`}
                data-testid={`upgrade-card-${p.key}`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-display text-base text-white">{p.name}</div>
                  {p.popular && (
                    <span className="text-[9px] font-mono-data tracking-wider uppercase px-1.5 py-0.5 rounded-sm border border-[#0A84FF]/40 bg-[#0A84FF]/10 text-[#0A84FF]">
                      Popular
                    </span>
                  )}
                </div>
                <div>
                  <div className="font-mono-data text-2xl text-white">{p.price}</div>
                  <div className="text-[11px] text-[#737373] mt-0.5 font-mono-data uppercase tracking-wider">{p.period}</div>
                </div>
                <ul className="space-y-1.5 text-[12px] text-[#E5E5E5]">
                  {p.perks.map((perk) => (
                    <li key={perk} className="flex gap-2">
                      <Check className="w-3.5 h-3.5 text-[#32D74B] shrink-0 mt-0.5" />
                      <span>{perk}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between gap-3 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              data-testid="upgrade-modal-dismiss"
              className="bg-transparent hover:bg-[#1F1F1F] text-[#A3A3A3] border border-[#262626]"
            >
              Maybe later
            </Button>
            <Link
              to="/pricing"
              onClick={onClose}
              className="inline-flex items-center gap-2 h-10 px-5 rounded-sm bg-[#0A84FF] hover:bg-[#409CFF] text-white font-medium tracking-tight transition-colors"
              data-testid="upgrade-modal-cta"
            >
              <Sparkles className="w-4 h-4" strokeWidth={1.8} />
              View all plans
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
